import { LeaseWithApartment } from "@boark/shared/types/lease-with-apartment";
import { formatResidencyName } from "@boark/shared/utils/apartment-residency";
import { Outlet, useSearchParams } from "react-router-dom";

import { ApartmentLink } from "../../components/apartment-link";
import { Button } from "../../components/button";
import { H1 } from "../../components/h1";
import { ResidencyName } from "../../components/residency-name";
import { Column, Filter, Table } from "../../components/table";
import { useGetApartments } from "../../queries/get-apartments";
import { useGetLeases } from "../../queries/lease";
import { formatApartmentLabel } from "../../services/apartment";
import { useLocale } from "../../stores/locale";
import {
  compareContractLength,
  formatContractLength,
} from "../../utils/contract-length";
import { createIssueRouteForRecord } from "../../utils/create-issue-route";
import { formatDate } from "../../utils/date";
import { isCurrent } from "../../utils/is-current";
import { recordIdToId } from "../../utils/record-id-to-id";

type LeaseWithApartmentData = LeaseWithApartment & {
  active: boolean;
};

export function Leases() {
  const { locale: t, get: getLocale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: leases, isLoading: isLeasesLoading } = useGetLeases();
  const { data: apartments } = useGetApartments();

  function updateSearchParams(params: Record<string, string>) {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      ...params,
    });
  }

  return (
    <>
      <Outlet />
      <div className="w-full min-h-full overflow-x-auto">
        <div className="flex justify-between items-center">
          <H1>{t.lease.leases}</H1>
          <Button route="/admin/register/lease/create">{t.lease.create}</Button>
        </div>
        <Table
          actions={(row) => [
            {
              label: getLocale("lease.viewApartment"),
              route: `/admin/register/apartment/?filterBy=${encodeURIComponent(
                JSON.stringify([
                  {
                    field: "index",
                    value: row.apartment.index,
                    operator: "eq",
                  },
                ]),
              )}`,
            },
            {
              label: t.issue.create,
              route: createIssueRouteForRecord({
                type: "lease",
                recordId: recordIdToId(row.id),
                label: `${formatApartmentLabel(row.apartment)} — ${formatResidencyName(row)} (${formatDate(row.fromDate)})`,
              }),
            },
          ]}
          data={(leases ?? []).map((lease) => ({
            ...lease,
            active: isCurrent(lease),
          }))}
          editRouteButton={(row) =>
            `/admin/register/lease/${recordIdToId(row.id)}/edit`
          }
          filterBy={
            JSON.parse(searchParams.get("filterBy") || "[]") as Filter[]
          }
          loading={isLeasesLoading}
          locale={{ yes: t.yes, no: t.no, actions: t.action.actions, empty: t.empty }}
          removeRouteButton={(row) =>
            `/admin/register/lease/${recordIdToId(row.id)}/delete`
          }
          sortBy={searchParams.get("sortBy") || "apartment.index"}
          sortByDirection={
            searchParams.get("sortByDirection") as "asc" | "desc"
          }
          onFilter={(value: Filter[]) => {
            updateSearchParams({
              filterBy: JSON.stringify(value),
            });
          }}
          onSort={(field, direction) => {
            updateSearchParams({
              sortBy: field,
              sortByDirection: direction,
            });
          }}
        >
          <Column
            body={(row: LeaseWithApartmentData) => (
              <ApartmentLink apartment={row.apartment} />
            )}
            field="apartment.index"
            filterableBy="select"
            filterOptions={(data: LeaseWithApartmentData[]) =>
              apartments
                ?.filter((apartment) =>
                  data.some((lease) => lease.apartment.id === apartment.id),
                )
                .map((apartment) => ({
                  label: (
                    <>
                      #{apartment.index} - {apartment.streetNumber}:
                      {apartment.apartmentNumber} ({apartment.objectNumber})
                    </>
                  ),
                  value: apartment.index,
                })) || []
            }
          >
            {t.apartment.apartment}
          </Column>
          <Column
            body={(row: LeaseWithApartmentData) => (
              <ResidencyName residency={row} />
            )}
            field="name"
            sortCallback={(
              a: LeaseWithApartmentData,
              b: LeaseWithApartmentData,
              _field: string,
              direction: "asc" | "desc",
            ) => {
              const result = formatResidencyName(a).localeCompare(
                formatResidencyName(b),
              );
              if (direction === "asc") {
                return result;
              }
              return -result;
            }}
          >
            {getLocale("lease.residents")}
          </Column>
          <Column
            body={(row: LeaseWithApartmentData) => formatDate(row.fromDate)}
            field="fromDate"
            filterableBy="date"
          >
            {getLocale("lease.fromDate")}
          </Column>
          <Column
            body={(row: LeaseWithApartmentData) =>
              (row.toDate && formatDate(row.toDate)) ?? "-"
            }
            field="toDate"
            filterableBy="date"
          >
            {getLocale("lease.toDate")}
          </Column>
          <Column
            body={(row: LeaseWithApartmentData) =>
              formatContractLength(row.fromDate, row.toDate, {
                year: t.year,
                years: t.years,
                month: t.month,
                months: t.months,
              })
            }
            field="contractLength"
            sortCallback={(
              a: LeaseWithApartmentData,
              b: LeaseWithApartmentData,
              _field: string,
              direction: "asc" | "desc",
            ) => compareContractLength(a, b, direction)}
          >
            {getLocale("lease.contractLength")}
          </Column>
          <Column
            body={(row: LeaseWithApartmentData) => (row.active ? "Ja" : "Nej")}
            field="active"
            filterableBy="select"
            filterOptions={() => [
              { label: t.yes, value: true },
              { label: t.no, value: false },
            ]}
          >
            {getLocale("lease.active")}
          </Column>
          <Column field="issueCount">{t.lease.issueCount}</Column>
        </Table>
      </div>
    </>
  );
}
